import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { Stack } from "@mui/material";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { APP_ICONS } from "../../../../assets";
import AppLoader from "../../../../components/AppLoader";
import PaperContainer from "../../../../components/Containers/PaperContainer";
import GlobalButton from "../../../../components/CustomButton";
import DynamicPaginatedTable from "../../../../components/Tables/DynamicPaginatedTable";
import TextInput from "../../../../components/TextInputs";
import useDebounce from "../../../../hooks/useDebounce";
import { setPromotionalBlockCurrentPage, setPromotionalBlockPageLimit, setPromotionalBlockTotalCount, setPromotionalBlockTotalPages } from "../../../../lib/slices/pagination/promotionalBlockPageSlice";
import { setPromotionalBlockName } from "../../../../lib/slices/searchKeysSlice";
import { getAllPromotionalBlocks, updatePromotionalBlock } from "../../../../services/promotionalBlockApiFn";
import { handleOpenSnackBar } from "../../../../utils";
import { AddEditPromotionalBlockDialog } from "./AddEditPromotionalBlockDialog";

// Header Data For Promotional Block Table
const headerData = [
    "createdAt",
    "title",
    "page",
    "positionType",
    "startDate",
    "endDate",
    "status",
    "actions",
];

// Table Rows For Promotional Block Table
const tableRows = [
    "created_date",
    "promotional_block_title",
    "promotional_block_page",
    "promotional_block_position_type",
    "promotional_block_start_date",
    "promotional_block_end_date",
    "status",
    "actions",
];

// Promotional Block Component
export function PromotionalBlock() {
    // Dispatch Hook
    const dispatch = useDispatch();
    // Open Dialog Ref
    const openDialog = useRef(null);
    // Loading State
    const [isLoading, setIsLoading] = useState(true);
    // All Promotional Blocks
    const [allPromotionalBlocks, setAllPromotionalBlocks] = useState([]);
    // Translation Hook
    const { t } = useTranslation(["catalogue", "common"]);
    // Search Keys
    const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
    // Get Pagination for Promotional Blocks
    const pagination = useSelector((state) => state.promotionalBlockPaginationReducer);
    // Debounced Search Term
    const debouncedSearchTerm = useDebounce(searchKeys.promotionalBlockName, 1000, true, () => {
        dispatch(setPromotionalBlockCurrentPage(1));
    });

    // Fetch All Promotional Blocks
    const fetchAllPromotionalBlocks = async (search, page, limit, startDate, endDate) => {
        try {
            const params = {
                search,
                page,
                limit,
                startDate,
                endDate,
            };
            setIsLoading(true);
            const response = await getAllPromotionalBlocks(params);
            if (response.data.status === "success") {
                let data = response?.data.data?.data
                const pagination = response?.data.data.pagination;
                setAllPromotionalBlocks(data);
                dispatch(setPromotionalBlockPageLimit(pagination.limit));
                dispatch(setPromotionalBlockCurrentPage(pagination.page));
                dispatch(setPromotionalBlockTotalCount(pagination.total));
                dispatch(setPromotionalBlockTotalPages(pagination.totalPages));
            } else {
                handleOpenSnackBar("error", response.data.message);
            }
        } catch (error) {
            handleOpenSnackBar("error", error.message);
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch All Promotional Blocks On Component Mount
    useEffect(() => {
        fetchAllPromotionalBlocks(
            debouncedSearchTerm,
            pagination.page,
            pagination.limit,
            searchKeys.promotionalBlockStartDate,
            searchKeys.promotionalBlockEndDate);
    }, [
        debouncedSearchTerm,
        pagination.page,
        pagination.limit,
    ]);

    // Handle Edit Promotional Block
    const handleEdit = (params) => {
        openDialog.current.openDialog({
            type: "edit",
            data: params.row,
            getData: fetchAllPromotionalBlocks,
        });
    };

    // handle page change
    const handlePageChange = (event, newPage) => {
        const page = newPage + 1;
        dispatch(setPromotionalBlockCurrentPage(page));
    };

    // handle rows per page change
    const handleRowsPerPageChange = (event) => {
        console.log(event, "event");
        const limit = parseInt(event.target.value, 10);
        dispatch(setPromotionalBlockPageLimit(limit));
        dispatch(setPromotionalBlockCurrentPage(1));
    };

    // Handle Change Status Promotional Block
    const handleChangeStatus = async (event, row, key) => {
        try {
            let payload = {
                [key]: event,
            };
            const response = await updatePromotionalBlock(row._id, payload);
            if (response.data.status === "success") {
                fetchAllPromotionalBlocks(
                    searchKeys.promotionalBlockName,
                    pagination.page,
                    pagination.limit,
                    searchKeys.promotionalBlockStartDate,
                    searchKeys.promotionalBlockEndDate);
                handleOpenSnackBar("success", response.data.message);
            } else {
                handleOpenSnackBar("error", response.data.message);
            }
        } catch (error) {
            handleOpenSnackBar("error", error.message);
        }
    };


    // Prepare Export Data For Promotional Block
    const prepareExportData = () => {
        return allPromotionalBlocks?.map((item) => ({
            "Created At": moment(item.createdAt).format("MM-DD-YYYY") || "-",
            "Updated At": moment(item.updatedAt).format("MM-DD-YYYY") || "-",
            "Title In Romanian": item.title?.ro || "-",
            "Title In English": item.title?.en || "-",
            "Image Url In English": item.image?.en || "-",
            "Image Url In Romanian": item.image?.ro || "-",
            "Redirect Link in English": item?.link?.en || "-",
            "Redirect Link in Romanian": item?.link?.ro || "-",
            "Page": item.page || "-",
            "Section": item.section || "-",
            "Start Date": item.startDate || "-",
            "End Date": item.endDate || "-",
            "Status": item.status ? "Active" : "Inactive",
        }));
    };

    const renderHeaderAction = () => {
        return (
            <Stack
                gap={1}
                flexGrow={1}
                direction={"row"}
                alignItems={"center"}
                justifyContent={"flex-end"}
            >
                <GlobalButton
                    variant={"contained"}
                    btnText={"addNew"}
                    handleClickBtn={() => {
                        openDialog.current.openDialog({
                            type: "add",
                            data: null,
                            getData: fetchAllPromotionalBlocks,
                        });
                    }}
                    styleProps={{
                        width: "auto",
                        height: 40,
                    }}
                />
                <CSVLink
                    data={prepareExportData()}
                    filename={"promotional-blocks.csv"}
                    className="csv-link"
                    target="_blank"
                >
                    <GlobalButton
                        variant={"export_button"}
                        btnText={"export"}
                        startIcon={<img src={APP_ICONS.exportBlockIcon} width={20} />}
                        styleProps={{
                            width: "auto",
                            height: 40,
                        }}
                    />
                </CSVLink>
            </Stack>
        );
    };

    const renderSearchAction = () => {
        return (
            <Stack p={2} direction={{ md: "row" }} bgcolor={"primary.light"}>
                <TextInput
                    styleProps={{ width: { xs: "100%", md: "35%", xl: "30%" } }}
                    InputStartIcon={<img src={APP_ICONS.searchGreyIcon} width={25} />}
                    InputEndIcon={
                        searchKeys.promotionalBlockName && (
                            <CancelOutlinedIcon
                                fontSize="small"
                                sx={{ cursor: "pointer" }}
                                onClick={() => {
                                    dispatch(
                                        setPromotionalBlockName("")
                                    );
                                }}
                            />
                        )
                    }
                    placeholder={t("searchByName", { ns: "common" })}
                    value={searchKeys.promotionalBlockName}
                    onChange={(e) => {
                        dispatch(
                            setPromotionalBlockName(e.target.value)
                        );
                    }}
                />
            </Stack>
        );
    };

    const renderTable = useMemo(() => {
        return (
            <DynamicPaginatedTable
                minWidth={1200}
                tableHeader={headerData}
                displayRows={tableRows}
                tableData={allPromotionalBlocks || []}
                showActions={true}
                showEdit={true}
                handleEdit={handleEdit}
                handleChangeStatus={(e, row, key) => handleChangeStatus(e, row, "status")}
                paginatedData={pagination}
                showDynamicPagination={true}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 20, 30, 50, 100]}
                isLoading={isLoading}
            />
        );
    }, [allPromotionalBlocks, isLoading]);

    return (
        <React.Fragment>
            <PaperContainer
                title={t("promotionalImages")}
                subtitle={t("viewPromotionalImages")}
                headerActions={renderHeaderAction()}
                searchActions={renderSearchAction()}
            >
                {renderTable}
            </PaperContainer>

            <AddEditPromotionalBlockDialog ref={openDialog} />

        </React.Fragment>
    );
}


